import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { Link } from 'react-router-dom';
import TopNav from '../TopNav/TopNav';
import Footer from '../MainPage/Footer';
import { usePermissions } from '../../hooks/usePermissions';
import { createPurchaseOrderClient, FILE_ACCEPT, PURCHASE_REQUEST_LIST_URL, requestFields, validateRequest } from './purchaseOrderService';

export default function FormsPage() {
  const { instance, accounts } = useMsal();
  const { canEdit, isLoading: isPermissionsLoading } = usePermissions();
  const canSubmit = !isPermissionsLoading && canEdit('home.forms');
  const client = useMemo(() => createPurchaseOrderClient(instance, accounts[0]), [instance, accounts]);
  const [values, setValues] = useState({});
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uncertain, setUncertain] = useState(false);
  const submitting = useRef(false);
  const fileInput = useRef(null);
  const [accessError, setAccessError] = useState('');
  const [listTitle, setListTitle] = useState('');
  useEffect(() => {
    let active = true;
    client.checkAccess().then(list => { if (active) { setAccessError(''); setListTitle(list.Title); } }).catch(failure => {
      if (active) setAccessError(failure.message);
    });
    return () => { active = false; };
  }, [client]);

  const submit = async (event) => {
    event.preventDefault();
    if (!canSubmit || submitting.current || uncertain || (savedId && !pendingFiles.length)) return;
    const validation = validateRequest(values, files);
    setErrors(validation);
    if (Object.keys(validation).length) return;
    submitting.current = true;
    setBusy(true);
    setError('');
    let id = savedId;
    const attachments = id ? pendingFiles : files;
    try {
      if (!id) {
        id = await client.create(values, files);
        setSavedId(id);
        setPendingFiles(attachments);
      }
      if (attachments.length) {
        const failed = await client.upload(id, attachments);
        setPendingFiles(failed);
        if (failed.length) setError('Your request was saved, but some attachments could not be uploaded. Retry the attachments below.');
      }
    } catch (failure) {
      if (id) setError('Your request was saved, but attachment upload could not be confirmed. Retry the attachments below.');
      else {
        setError(failure.message);
        setUncertain(Boolean(failure.uncertain));
      }
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  };

  const reset = () => {
    setValues({}); setFiles([]); setErrors({}); setError(''); setSavedId(null); setPendingFiles([]); setUncertain(false);
    if (fileInput.current) fileInput.current.value = '';
  };

  return (
    <div>
      <TopNav />
      <main className="container py-4" style={{ maxWidth: 1000, paddingBottom: '6rem' }}>
        <Link to="/" className="d-inline-block mb-3">&larr; Home</Link>
        <h1>Forms</h1>
        <section className="card shadow-sm mt-3 mb-5" aria-labelledby="purchase-request-title">
          <div className="card-header bg-light p-4">
            <h2 id="purchase-request-title" className="h4 mb-2">Purchase Order Request</h2>
            <p className="mb-0 text-muted">Please provide as much information as possible to aid in the purchasing process.</p>
          </div>
          <div className="card-body p-4">
            {listTitle && <p className="small text-muted">Requests are saved to {listTitle} in SharePoint.</p>}
            {accessError && <div className="alert alert-warning" role="alert">{accessError}</div>}
            {savedId && <div className={`alert ${pendingFiles.length ? 'alert-warning' : 'alert-success'}`} role="status">
              Request #{savedId} saved{pendingFiles.length ? '; attachments are pending.' : ' successfully.'}{' '}
              <a href={`${PURCHASE_REQUEST_LIST_URL}/DispForm.aspx?ID=${savedId}`} target="_blank" rel="noopener noreferrer">View request</a>
            </div>}
            {error && <div className="alert alert-danger" role="alert">{error}{' '}
              {uncertain && <a href={PURCHASE_REQUEST_LIST_URL} target="_blank" rel="noopener noreferrer">Check existing requests</a>}
            </div>}
            <form onSubmit={submit} noValidate aria-labelledby="purchase-request-title">
              {!canSubmit && <p className="text-muted" role="status">You have read-only access. Edit permission is required to submit requests or upload attachments.</p>}
              <p className="small text-muted">Fields marked * are required. Submitted as {accounts[0]?.name || accounts[0]?.username}.</p>
              {Object.keys(errors).length > 0 && <div className="alert alert-danger" role="alert">Please correct the highlighted fields.</div>}
              <fieldset disabled={!canSubmit || busy || Boolean(savedId) || uncertain}>
                <legend className="visually-hidden">Purchase order details</legend>
                <div className="row g-3">
                  {requestFields.map(field => {
                    const id = `purchase-${field.key}`;
                    const props = {
                      id, name: field.key, required: field.required,
                      className: `form-control ${errors[field.key] ? 'is-invalid' : ''}`,
                      value: values[field.key] || '',
                      'aria-invalid': Boolean(errors[field.key]),
                      'aria-describedby': errors[field.key] ? `${id}-error` : field.help ? `${id}-help` : undefined,
                      onChange: event => setValues(current => ({ ...current, [field.key]: event.target.value })),
                    };
                    return <div key={field.key} className={field.multiline ? 'col-12' : 'col-12 col-md-6'}>
                      <label htmlFor={id} className="form-label">{field.label}{field.required && ' *'}</label>
                      {field.multiline ? <textarea {...props} rows={3} /> : <input {...props} type={field.type || 'text'} step={field.type === 'number' ? 'any' : undefined} maxLength={!field.type ? 255 : undefined} />}
                      {field.help && <div className="form-text" id={`${id}-help`}>{field.help}</div>}
                      {errors[field.key] && <div className="invalid-feedback" id={`${id}-error`}>{errors[field.key]}</div>}
                    </div>;
                  })}
                  <div className="col-12">
                    <label htmlFor="purchase-files" className="form-label">Supporting Documents</label>
                    <input ref={fileInput} id="purchase-files" type="file" multiple accept={FILE_ACCEPT}
                      className={`form-control ${errors.files ? 'is-invalid' : ''}`} aria-invalid={Boolean(errors.files)}
                      aria-describedby={errors.files ? 'purchase-files-error' : 'purchase-files-help'}
                      onChange={event => setFiles(Array.from(event.target.files || []))} />
                    <div className="form-text" id="purchase-files-help">Up to 2 files, 10 MB each. Word, Excel, PowerPoint, PDF, images, video, or audio.</div>
                    {errors.files && <div className="invalid-feedback" id="purchase-files-error">{errors.files}</div>}
                  </div>
                </div>
              </fieldset>
              {pendingFiles.length > 0 && <p className="mt-3">Pending attachments: {pendingFiles.map(file => file.name).join(', ')}</p>}
              <div className="d-flex flex-wrap gap-3 align-items-center mt-4">
                {(!savedId || pendingFiles.length > 0) && <button type="submit" className="btn btn-primary" disabled={!canSubmit || busy || uncertain}>
                  {busy ? 'Saving…' : savedId ? 'Retry attachments' : 'Submit request'}
                </button>}
                {savedId && !pendingFiles.length && !busy && <button type="button" className="btn btn-primary" onClick={reset}>Submit another request</button>}
                {uncertain && <button type="button" className="btn btn-outline-secondary" onClick={() => { setUncertain(false); setError(''); }}>I checked the list; this request was not saved</button>}
                <a href={PURCHASE_REQUEST_LIST_URL} target="_blank" rel="noopener noreferrer">Open purchase requests</a>
              </div>
            </form>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
